import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Clock, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BLOG_POSTS, BUSINESS, LOGO_URL } from "@/lib/siteData";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const BlogPost = () => {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <main
        data-testid="blog-post-not-found"
        className="min-h-screen bg-white text-green-950 flex flex-col items-center justify-center px-6 text-center"
      >
        <h1 className="text-3xl font-extrabold" style={{ fontFamily: "Outfit, sans-serif" }}>
          Post not found
        </h1>
        <p className="mt-3 text-slate-600">The article you&apos;re looking for doesn&apos;t exist.</p>
        <Link to="/" className="mt-6">
          <Button data-testid="blog-notfound-home" className="bg-green-700 hover:bg-green-800 rounded-full">
            Back to home
          </Button>
        </Link>
      </main>
    );
  }

  return (
    <main data-testid="blog-post-page" className="bg-white text-green-950">
      <header className="border-b border-green-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" data-testid="blog-post-brand" className="flex items-center gap-3 font-bold text-green-900">
            <span className="inline-flex items-center justify-center rounded-full overflow-hidden w-10 h-10 bg-white shadow-md ring-1 ring-green-100">
              <img src={LOGO_URL} alt="Pond & Garden logo" className="w-10 h-10 object-cover" />
            </span>
            <span className="hidden sm:inline text-base font-extrabold" style={{ fontFamily: "Outfit, sans-serif" }}>
              Pond &amp; Garden
            </span>
          </Link>
          <Link
            to="/#blog"
            data-testid="blog-post-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-green-700 hover:text-green-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <span className="text-xs font-semibold uppercase tracking-widest text-green-700">
          {post.category}
        </span>
        <h1
          data-testid="blog-post-title"
          className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-green-950"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {post.title}
        </h1>
        <div className="mt-5 flex items-center gap-5 text-sm text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="w-4 h-4" /> {formatDate(post.date)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-4 h-4" /> {post.readTime}
          </span>
        </div>

        <div className="mt-8 rounded-2xl overflow-hidden border border-green-100">
          <img src={post.cover} alt={post.title} className="w-full h-auto object-cover" />
        </div>

        <div className="mt-10 space-y-6">
          {post.body.map((block, i) =>
            block.type === "h" ? (
              <h2
                key={i}
                className="text-2xl font-bold text-green-950 pt-2"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {block.text}
              </h2>
            ) : (
              <p key={i} className="text-slate-700 text-lg leading-relaxed">
                {block.text}
              </p>
            )
          )}
        </div>

        <div
          data-testid="blog-post-cta"
          className="mt-14 rounded-2xl bg-green-50 border border-green-100 p-8 text-center"
        >
          <h3 className="text-2xl font-bold text-green-950" style={{ fontFamily: "Outfit, sans-serif" }}>
            Need a hand with your pond or garden?
          </h3>
          <p className="mt-2 text-slate-600">
            Covering properties within approximately 30 minutes&apos; travel of East Grinstead.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <a href="/#contact">
              <Button data-testid="blog-post-quote-cta" className="bg-green-700 hover:bg-green-800 rounded-full px-6">
                Get in Touch
              </Button>
            </a>
            <a href={`tel:${BUSINESS.phoneTel}`}>
              <Button
                variant="outline"
                data-testid="blog-post-call-cta"
                className="rounded-full px-6 border-green-300 text-green-800 hover:bg-green-100"
              >
                <Phone className="w-4 h-4 mr-2" /> {BUSINESS.phone}
              </Button>
            </a>
          </div>
        </div>
      </article>
    </main>
  );
};

export default BlogPost;
